/* eslint-disable max-lines */
import {
  OnChanges,
  SimpleChanges, AfterViewInit, OnDestroy,
  ChangeDetectorRef, AfterContentChecked, Component, OnInit
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { UntypedFormGroup, UntypedFormControl, Validators, FormArray, FormGroup } from '@angular/forms';
import { Observable, of, iif, zip, tap, map, switchMap, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { isNullOrUndefined } from '@app/util/util';
import { environment } from '@environment';
import { BookingBaseContainer } from '@booking/containers/booking-base.container';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { BookingFacade } from '@booking/booking.facade';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { LoyaltyCreateEvent, LoyaltyUpdateEvent } from '@pax/entities/pax-ui';
import { LoyaltyResponse } from '@pax/entities/traveler-pax';
import { PaxFormService } from '@pax/services/pax-form.service';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { AppFacade } from '@app/app.facade';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { DEFAULT_EMAIL_CONTROL, DEFAULT_PHONE_CONTROL } from '@forms/contact-form/constants/contact-form.constants';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { BookingArrangementVM, SubmitBookingArrangement } from '@booking/entities/booking-arrangement';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { airBookingPayloadMapper } from '@booking/mappers/air-booking-payload.mapper';
import { assistanceSearchAmplitudePropsMapper } from '@app/modules/core/mappers/assistance-amplitude.mapper';

@Component({
  template: '',
  standalone: false
})
export class BookingFlightsBaseContainer extends BookingBaseContainer implements OnInit, OnChanges, AfterViewInit,
  OnDestroy, AfterContentChecked {

  public bookingFlightsForm: UntypedFormGroup;
  public mappedValidationID: string | string[];
  public amplitudeValidationResponse: AirValidationResponse;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected facade: BookingFacade,
    protected viewportScroller: ViewportScroller,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe
  ) {
    super(
      route, router, cdRef, appFacade, viewportScroller, facade, billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
  }

  ngOnInit(): void {
    this.createForm();
    this.listenFormChanges(this.bookingFlightsForm);
    this.isLoadingBookingDelay$ = this.isLoadingBookingDelaySubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['user'] && this.bookingFlightsForm) {
      this.setUserData();
    }
  }

  ngAfterContentChecked(): void {
    this.checkPaxSection();
  }

  ngAfterViewInit(): void {
    if (this.user && this.bookingFlightsForm) this.setUserData();
  }

  ngOnDestroy(): void {
    this.assistanceFacade.resetAssistanceSearchInfo();
    if (!isNullOrUndefined(this.isLoadingBookingDelay$)) {
      this.isLoadingBookingDelay$.unsubscribe();
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get flightClass$(): Observable<FlightClass> {
    return this.facade.flightClass$;
  }

  get frequentFlyerPrograms$(): Observable<FrequentFlyerResponse[]> {
    return this.facade.frequentFlyerPrograms$;
  }

  get isAnInternationalTrip$(): Observable<boolean> {
    return this.appFacade.isAnInternationalSearch$;
  }
  
  get passengerArrangementsState$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangementsState$;
  }

  public createLoyalty(loyaltyEven: LoyaltyCreateEvent, id: number): void {
    if (!loyaltyEven.create) {
      this.paxFacade.addLoyaltyProgram(loyaltyEven.loyalty, id);
    } else this.paxFacade.createLoyaltyProgram(loyaltyEven.loyalty, id);
  }

  public selectLoyalty(loyalty: LoyaltyResponse, id: number): void {
    this.paxFacade.setLoyaltyProgram(loyalty, id);
  }

  public deleteLoyalty(loyalty: LoyaltyResponse, id: number): void {
    this.paxFacade.deleteLoyaltyProgram(loyalty, id);
  }

  public updateLoyalty(loyaltyEven: LoyaltyUpdateEvent, id: number): void {
    if (!loyaltyEven.update) {
      this.paxFacade.addLoyaltyProgram(loyaltyEven.loyalty, id);
    } else { 
      this.paxFacade.updateLoyaltyProgram(loyaltyEven.loyalty, id); 
    }
  }

  public removeDefaultPax(id: number): void {
    this.paxFacade.removeDefaultPax(id);
  }

  public setShowBillingModule(value: boolean): void {
    this.showBillingModule = value;
    this.resetbillingForm(this.bookingFlightsForm);
  }

  public submitBookingOptions(isDisabled: boolean): void {
    zip(
      this.submitToBookServiceSubscription$(this.bookingFlightsForm.value, null),
      this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(take(1)),
      this.assistanceFacade.assistanceSearchInfo$
    ).pipe(
      switchMap((([{ props, trigger }, searchProps, assistanceSearchInfo]) => (
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_ASSISTANCE_MODAL,
          assistanceSearchAmplitudePropsMapper(assistanceSearchInfo?.Results?.[0]),
          searchProps
        ).pipe(
          tap(eventProps => {
            if (this.isActivePaidAssistance ) {
              this.fetchAssistanceModal(this.bookingFlightsForm, eventProps, isDisabled);
            } else {
              this.submitByBookServiceAction({ props, trigger }, this.bookingFlightsForm, this.travelerBookingProvider);
            }
          })
        )
      )))
    ).subscribe().unsubscribe();
  }

  public submitBookingOptionsByAssistance(assistanceInfo: { optionId: number, searchId: string }): void {
    this.submitToBookServiceSubscription$(this.bookingFlightsForm.value, assistanceInfo).pipe(
      tap(({ props, trigger }) => this.submitByBookServiceAction({ props, trigger },
        this.bookingFlightsForm, this.travelerBookingProvider))
    ).subscribe().unsubscribe();
  }

  protected createForm(): void {
    this.bookingFlightsForm = new UntypedFormGroup({
      [BOOKING_FORM_INPUTS.TRAVELERS]: new FormArray([]),
      [BOOKING_FORM_INPUTS.PHONE]: DEFAULT_PHONE_CONTROL,
      [BOOKING_FORM_INPUTS.EMAIL]: DEFAULT_EMAIL_CONTROL,
      [BOOKING_FORM_INPUTS.COMMENTS]: new UntypedFormControl(''),
      [BOOKING_FORM_INPUTS.TERMS]: new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]),
      [BOOKING_FORM_INPUTS.BILLING]: new FormGroup({})
    });
    if (this.individualTerms) {
      this.bookingFlightsForm.addControl(BOOKING_FORM_INPUTS.EXTRA_TERMS, new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]));
    }
  }

  private setUserData(): void {
    this.bookingFlightsForm.patchValue({
      [BOOKING_FORM_INPUTS.EMAIL]: this.user?.email ?? '',
      [BOOKING_FORM_INPUTS.PHONE]: this.user?.phone ?
        `${this.user?.country_code ? this.user.country_code.replace('+', '') :
          environment.countryInfo.phone_prefix.replace('+', '')}${this.user.phone}` : ''
    });
  }

  protected submitToBookServiceSubscription$(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): Observable<SubmitBookingArrangement> {
    const searchby = this.travelerBookingProvider === TravelBookingType.BUSES ? SearchBy.BUSES : SearchBy.FLIGHTS;
    return zip(
      this.route.paramMap.pipe(take(1)),
      this.billingFacade.defaultBillingInfo$.pipe(take(1)),
      this.propertiesAmplitudeService.searchProperties$(searchby).pipe(take(1)),
      this.bookingReady$.pipe(take(1))
    ).pipe(
      map(([params, defaultBillingInfo, searchProps, isReadyBook]) => ({
        params, defaultBillingInfo, searchProps,
        isReadyBook: !!this.bookingFlightsForm.valid && !!isReadyBook
      })),
      switchMap(({ isReadyBook, searchProps, params, defaultBillingInfo }) => iif(
        () => !!isReadyBook,
        zip(
          of(
            airBookingPayloadMapper(
              this.bookingFlightsForm,
              this.mappedValidationID,
              arrangement,
              defaultBillingInfo,
              assistanceInfo,
              this.travelerBookingProvider
            )
          ),
          this.bookingAmplitudeService.airCheckoutTriggerAmplitudeEntity$(
            this.amplitudeValidationResponse, params, searchProps
          ).pipe(take(1))
        ),
        of([null, null])
      )),
      map(([props, trigger]) => ({ props, trigger }))
    );
  }
}
